/**
 * Trade-in actions ported from reware-admin's Trade-ins portal
 * (tradeins-api.js). Each hits the same admin_graphql mutation the admin
 * portal uses.
 */
import { execAdmin } from '@src/apis/reware/utils/exec';

/**
 * The mutation fields this module uses. A free `string` here would let a field name that is not
 * in the query reach the guard below: the mutation would run, the lookup would yield undefined,
 * and the operator would be told "nothing was changed" about an operation that did happen.
 */
type TradeinMutationField =
  | 'approve'
  | 'cancel'
  | 'initiateSendback'
  | 'packSendback'
  | 'updatePaymentPreference'
  | 'issueGiftcard'
  | 'sendMitiInitiationEmail'
  | 'saveNotes'
  | 'updateSendbackOptIn'
  | 'updateShippingAddress';

interface TradeinMutationResponse {
  tradein: Partial<Record<TradeinMutationField, { id: number | null } | null>> | null;
}

/**
 * Runs a trade-in mutation and verifies the root actually produced a result.
 *
 * The check is not optional. `execAdmin` throws only when the payload carries `errors[0].message`,
 * and `tradein(id:)` resolves `null` for an id that does not exist — no error at all. Awaiting and
 * discarding reported a green "Trade-in approved" / "Gift card issued" for operations that never
 * happened, including the ones that move money and inventory custody.
 *
 * It tests `.id`, not just the field object: a service that bails early can resolve a non-model
 * value, which graphql-js completes into a present-but-empty object rather than a null.
 *
 * Note what this does NOT catch. The mutation root is gated by `canEdit('tradeins')` with no
 * partner binding, and `resolver(Tradein)` looks the trade-in up by primary key — so an id
 * belonging to another partner resolves normally and the mutation runs. This guard covers the
 * not-found case only; it is not an authorization check.
 */
const runTradeinMutation = async <V>(
  tradeinId: number,
  field: TradeinMutationField,
  query: string,
  variables: V
): Promise<void> => {
  const data = await execAdmin<V, TradeinMutationResponse>(query, { variables });

  if (!data?.tradein?.[field]?.id) {
    throw new Error(`Trade-in ${tradeinId} did not accept the ${field} request — nothing was changed`);
  }
};

/**
 * Dollar amounts come from free-text dialog inputs. `parseFloat` happily yields a negative, and
 * the server has no reason to reject one, so a mistyped "-50" would issue a negative gift card.
 */
const requirePositiveAmount = (amount: number, label: string): void => {
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error(`${label} must be greater than zero`);
  }
};

export const approveTradein = async (tradeinId: number): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'approve',
    'mutation approve($tradeinId: Int!) { tradein(id: $tradeinId) { approve { id state } } }',
    { tradeinId }
  );
};

export const cancelTradein = async (tradeinId: number): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'cancel',
    'mutation cancel($tradeinId: Int!) { tradein(id: $tradeinId) { cancel { id } } }',
    { tradeinId }
  );
};

/**
 * NOT WIRED UP. TradeinActions deliberately does not offer this yet, because the ids have to
 * come from an operator's selection and there is no picker on this page.
 *
 * Do not derive the list from `canBeSupplierSendback`. The resolver passes `override: true`, so
 * the server ignores that flag entirely — and `canSendbackTradein`, the flag that would gate the
 * menu item, is itself true only when *no* item satisfies it (approve() auto-sends those back,
 * so the flag means "the automatic pass left nothing, CS may now hand-pick"). Filtering on it
 * therefore yields an empty list in exactly the case the action is available. reware-admin shows
 * an unfiltered checkbox table for this reason.
 *
 * The ids must also be *active* items: the server intersects them with getActiveTradeinItems,
 * and `tradeinItems` in our query carries no active scope, so inactive ids silently reduce to an
 * empty set and the service returns a scalar that fails to complete against a non-null id.
 */
export const initiateSendback = async (tradeinId: number, sendbackItemIds: number[]): Promise<void> => {
  if (!sendbackItemIds.length) {
    throw new Error('Select at least one item to send back');
  }

  await runTradeinMutation(
    tradeinId,
    'initiateSendback',
    `mutation initiateSendback($tradeinId: Int!, $sendbackItemIds: [Int]!) {
      tradein(id: $tradeinId) { initiateSendback(sendbackItemIds: $sendbackItemIds) { id state } }
    }`,
    { tradeinId, sendbackItemIds }
  );
};

/**
 * NOT WIRED UP. TradeinActions deliberately does not offer this yet: the mutation is only the
 * first half of the flow. reware-admin follows it immediately with setYpackageDimension, fed by
 * a form that requires height/width/length/weight and a label template. Firing this alone
 * creates, packs and drops the sendback package with no dimensions and no template, which
 * leaves downstream postage generation nothing to work from.
 *
 * Two things to keep if it is wired up later.
 *
 * `sendbackItemIds` must be the ids of the trade-in's *sendback-scoped* items (our query selects
 * them as `sendbackTradeinItems`), not items filtered on `canBeSupplierSendback` — the service
 * compares the list against getSendbackTradeinItems() and rejects the whole pack if one is
 * missing, and a CS-initiated sendback need not satisfy that flag at all.
 *
 * `sendbackMissingIds` is non-optional in practice despite being a nullable argument: the
 * service destructures it with no default and calls `.indexOf` on it per item. Omitting it
 * throws a TypeError server-side. Send an empty list when nothing is missing.
 *
 * Note the service is not transactional either way: pick() commits REQUESTED → PICKING before
 * anything that can fail, and once the trade-in is PICKING, canCreateSendbackPack is false and
 * this UI exposes no unpickSendback to recover with.
 */
export interface PackSendbackInput {
  tradeinId: number;
  /** Ids of the trade-in's sendback-scoped items. */
  sendbackItemIds: number[];
  /** Ids the operator could not find. Empty means nothing is missing. */
  sendbackMissingIds?: number[];
}

// An options object rather than three positionals: the two id arrays are the same type and
// adjacent, so a swap would compile and strand the trade-in in PICKING.
export const packSendback = async ({
  tradeinId,
  sendbackItemIds,
  sendbackMissingIds = [],
}: PackSendbackInput): Promise<void> => {
  if (!sendbackItemIds.length) {
    throw new Error('This trade-in has no sendback items to pack');
  }

  await runTradeinMutation(
    tradeinId,
    'packSendback',
    `mutation packSendback($tradeinId: Int!, $sendbackItemIds: [Int]!, $sendbackMissingIds: [Int]!) {
      tradein(id: $tradeinId) {
        packSendback(sendbackItemIds: $sendbackItemIds, sendbackMissingIds: $sendbackMissingIds) { id state sendbackState }
      }
    }`,
    { tradeinId, sendbackItemIds, sendbackMissingIds }
  );
};

export const switchPaymentToGiftcard = async (tradeinId: number): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'updatePaymentPreference',
    `mutation updatePaymentPreference($tradeinId: Int!, $paymentPreference: String) {
      tradein(id: $tradeinId) { updatePaymentPreference(paymentPreference: $paymentPreference) { id paymentPreference } }
    }`,
    { tradeinId, paymentPreference: 'GIFTCARD' }
  );
};

export interface IssueGiftcardInput {
  tradeinId: number;
  amount: number; // dollars
  email: string;
  reason: string;
  comment?: string;
}

export const issueGiftcard = async ({ tradeinId, amount, email, reason, comment }: IssueGiftcardInput): Promise<void> => {
  requirePositiveAmount(amount, 'Gift card amount');

  const cents = Math.round(amount * 100);
  await runTradeinMutation(
    tradeinId,
    'issueGiftcard',
    `mutation issueGiftcard($tradeinId: Int!, $reason: String!, $comment: String, $amount: Int!, $email: String!) {
      tradein(id: $tradeinId) { issueGiftcard(reason: $reason, note: $comment, amount: $amount, email: $email) { id amount } }
    }`,
    { tradeinId, reason, comment, amount: cents, email }
  );
};

export const emailShippingLabel = async (tradeinId: number): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'sendMitiInitiationEmail',
    'mutation sendMitiInitiationEmail($tradeinId: Int!) { tradein(id: $tradeinId) { sendMitiInitiationEmail { id } } }',
    { tradeinId }
  );
};

export const saveTradeinNotes = async (tradeinId: number, notes: string): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'saveNotes',
    `mutation saveTradeinNotes($tradeinId: Int!, $notes: String) {
      tradein(id: $tradeinId) { saveNotes(notes: $notes) { id notes } }
    }`,
    { tradeinId, notes }
  );
};

export const updateSendbackOptIn = async (tradeinId: number, optInSendback: boolean): Promise<void> => {
  await runTradeinMutation(
    tradeinId,
    'updateSendbackOptIn',
    `mutation updateSendbackOptIn($tradeinId: Int!, $optInSendback: Boolean) {
      tradein(id: $tradeinId) { updateSendbackOptIn(isSendbackOptIn: $optInSendback) { id isSendbackOptIn } }
    }`,
    { tradeinId, optInSendback }
  );
};

export interface UpdateShippingAddressInput {
  tradeinId: number;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export const updateShippingAddress = async (input: UpdateShippingAddressInput): Promise<void> => {
  const { tradeinId, ...address } = input;
  await runTradeinMutation(
    tradeinId,
    'updateShippingAddress',
    `mutation updateShippingAddress($tradeinId: Int!, $address1: String, $address2: String, $city: String, $state: String, $postalCode: String) {
      tradein(id: $tradeinId) {
        updateShippingAddress(address1: $address1, address2: $address2, city: $city, state: $state, postalCode: $postalCode) { id deliveryAddress }
      }
    }`,
    { tradeinId, ...address }
  );
};
